import { forwardRef, useState } from 'react';
import type { SelectHTMLAttributes } from 'react';
import clsx from 'clsx';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: Array<SelectOption>;
}

// Brand colors - exact match with elka-manager
const BRAND_BLUE = '#3C5FA8';
const ERROR_RED = '#ef4444';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helperText, options, className, onFocus, onBlur, style, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    // Focus ring via inline style - guaranteed to work across all apps
    const focusRingColor = error ? ERROR_RED : BRAND_BLUE;
    const selectStyle: React.CSSProperties = {
      ...(isFocused
        ? {
            boxShadow: `0 0 0 2px ${focusRingColor}`,
            borderColor: 'transparent',
            outline: 'none',
          }
        : {}),
      ...style,
    };

    return (
      <div className="w-full">
        {label && (
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {label}
            {props.required && <span className="mr-1 text-red-500">*</span>}
          </label>
        )}
        <select
          ref={ref}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={clsx(
            'min-h-[44px] w-full rounded-xl border px-3 py-2.5 md:min-h-0 md:py-2',
            'focus:outline-none',
            'disabled:cursor-not-allowed disabled:bg-gray-100',
            'text-base', // Prevents zoom on iOS
            error ? 'border-rose-300' : 'border-[#E2E8F0] hover:border-[#C7D7F0]',
            className
          )}
          style={selectStyle}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        {helperText && !error && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';
